import React from 'react';
import PropTypes from 'prop-types';
import { Plus, X } from 'lucide-react';
import AppSelect from 'src/components/common/AppSelect';
import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

// Sentinel for the "Any specialty" choice — maps to match_any_field:true with
// no field_of_study_id in the payload.
const ANY = '__ANY__';

export const makeEmptyQualGroup = () => ({
  is_mandatory: true,
  options: [],
  draftLevel: '',
  draftField: '',
});

/**
 * Grouped eligibility / qualification builder.
 *
 * Model (mirrors the API's qualification_requirements):
 *   groups[]           — AND'd together
 *     .is_mandatory    — Mandatory / Optional per group
 *     .options[]       — OR'd within the group
 *       .qualification_type   e.g. 'DEGREE' | 'MASTERS'
 *       .field_of_study_id    string id (absent when match_any_field)
 *       .match_any_field      true = any specialty
 *
 * Controlled: parent owns the array via `value` / `onChange`.
 */
export const QualificationGroups = ({
  value,
  onChange,
  levelOptions,
  fieldOptions,
}) => {
  const groups = value || [];

  const labelFor = (opts, val) =>
    opts.find((o) => String(o.value) === String(val))?.label ?? val;

  const optionText = (o) => {
    const lvl = labelFor(levelOptions, o.qualification_type);
    const fld = o.match_any_field
      ? 'Any specialty'
      : labelFor(fieldOptions, o.field_of_study_id);
    return `${lvl} in ${fld}`;
  };

  const setGroup = (gi, patch) =>
    onChange(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));

  const addGroup = () => onChange([...groups, makeEmptyQualGroup()]);
  const removeGroup = (gi) => onChange(groups.filter((_, i) => i !== gi));

  const addOption = (gi) => {
    const g = groups[gi];
    if (!g.draftLevel) return;
    const anyField = g.draftField === ANY || g.draftField === '';
    if (!anyField && !g.draftField) return;
    const opt = anyField
      ? {
          qualification_type: g.draftLevel,
          field_of_study_id: '',
          match_any_field: true,
        }
      : {
          qualification_type: g.draftLevel,
          field_of_study_id: g.draftField,
          match_any_field: false,
        };
    const dup = g.options.some(
      (o) =>
        o.qualification_type === opt.qualification_type &&
        !!o.match_any_field === !!opt.match_any_field &&
        String(o.field_of_study_id || '') ===
          String(opt.field_of_study_id || '')
    );
    setGroup(gi, {
      options: dup ? g.options : [...g.options, opt],
      draftLevel: '',
      draftField: '',
    });
  };

  const removeOption = (gi, oi) =>
    setGroup(gi, { options: groups[gi].options.filter((_, i) => i !== oi) });

  const fieldSelectOptions = [
    { value: ANY, label: 'Any specialty' },
    ...fieldOptions,
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Options in a box are alternatives (<b>OR</b>). Separate boxes must all
        be met (<b>AND</b>).
      </p>

      {groups.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          No education requirement set. Add one to get started.
        </div>
      )}

      {groups.map((g, gi) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={gi}>
          {gi > 0 && (
            <div className="flex items-center gap-2 py-1">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] font-bold tracking-[0.08em] text-foreground-strong border border-border rounded px-2 py-0.5">
                AND
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          <div className="rounded-lg border border-border border-l-[3px] border-l-primary/40 bg-card p-3.5">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-xs font-semibold text-muted-foreground mr-auto">
                Requirement {gi + 1}
              </span>
              <div className="inline-flex rounded-md border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setGroup(gi, { is_mandatory: true })}
                  className={cn(
                    'px-2.5 py-1 text-[11px] font-medium transition-colors',
                    g.is_mandatory
                      ? 'bg-brand-bg-subtle text-primary'
                      : 'bg-card text-muted-foreground'
                  )}
                >
                  Mandatory
                </button>
                <button
                  type="button"
                  onClick={() => setGroup(gi, { is_mandatory: false })}
                  className={cn(
                    'px-2.5 py-1 text-[11px] font-medium transition-colors',
                    !g.is_mandatory
                      ? 'bg-muted text-foreground-strong'
                      : 'bg-card text-muted-foreground'
                  )}
                >
                  Optional
                </button>
              </div>
              {groups.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeGroup(gi)}
                  title="Remove requirement"
                  className="w-6 h-6 rounded-md border border-border text-muted-foreground hover:text-ds-danger hover:border-ds-danger flex items-center justify-center"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {g.options.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                {g.options.map((o, oi) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={oi}>
                    {oi > 0 && (
                      <span className="text-[10px] font-bold text-ds-warning bg-ds-warning-subtle border border-ds-warning-tint rounded px-1.5 py-0.5">
                        OR
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-primary/60 text-primary bg-card px-2.5 py-1 text-xs font-medium">
                      {optionText(o)}
                      <button
                        type="button"
                        onClick={() => removeOption(gi, oi)}
                        title="Remove"
                        className="text-muted-foreground hover:text-ds-danger"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="text-xs italic text-muted-foreground mb-2.5">
                No qualifications yet — add at least one alternative below.
              </div>
            )}

            <div className="flex items-stretch gap-2 flex-wrap">
              <AppSelect
                options={levelOptions}
                value={g.draftLevel}
                onValueChange={(v) => setGroup(gi, { draftLevel: v })}
                placeholder="Level"
                className="w-[130px]"
              />
              <AppSelect
                options={fieldSelectOptions}
                value={g.draftField}
                onValueChange={(v) => setGroup(gi, { draftField: v })}
                placeholder="Specialty"
                className="flex-1 min-w-[160px]"
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => addOption(gi)}
                disabled={!g.draftLevel}
                className="h-9"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add option
              </Button>
            </div>
          </div>
        </React.Fragment>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addGroup}
        className="mt-2"
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        {groups.length === 0
          ? 'Add education requirement'
          : 'Add education requirement (AND)'}
      </Button>
    </div>
  );
};

QualificationGroups.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func.isRequired,
  levelOptions: PropTypes.arrayOf(PropTypes.shape({})),
  fieldOptions: PropTypes.arrayOf(PropTypes.shape({})),
};

QualificationGroups.defaultProps = {
  value: [],
  levelOptions: [],
  fieldOptions: [],
};

export default QualificationGroups;
